import React from 'react'
import {useDispatch} from 'react-redux'
import {css} from '@emotion/react'
import styled from '@emotion/styled'
import {WidgetLabelTypes} from 'utils/label'
import {
  getOfferWithCheapestNightlyPrice,
  getProviderCodeFromName,
  getRoundedNightlyDisplayTotalPrice
} from 'widgets/helpers/offer'

import {Divider} from '@daedalus/atlas/Divider'
import {Grid} from '@daedalus/atlas/helpers/Grid'
import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity
} from '@daedalus/core/src/analytics/types/Events'
import {TaxDisplayLogic} from '@daedalus/core/src/offer/types/TaxDisplayLogic'

import {Offer} from '../../../../types/partnersApi/types'
import {PrimaryOffer, Skeleton as PrimaryOfferSkeleton} from '../Primary'
import {
  SecondaryOfferList,
  Skeleton as SecondaryOfferSkeleton
} from '../Secondary'

interface OffersProps {
  numberOfRooms: number
  numberOfNights: number
  offers?: Offer[]
  taxDisplayLogic: TaxDisplayLogic
  partnerKey: string
  label?: string
}

const ComposedOffersWrapper = styled(Grid)(
  ({theme}) =>
    css`
      flex-basis: 264px;
      height: 264px;
      background-color: ${theme.colors.background.neutral.c000};
    `
)

export const ComposedOffersSkeleton = () => (
  <ComposedOffersWrapper container spacing="s000">
    <PrimaryOfferSkeleton />
    <SecondaryOfferSkeleton />
    <Divider sideMargin="s200" />
    <SecondaryOfferSkeleton />
  </ComposedOffersWrapper>
)

export const ComposedOffers = ({
  numberOfRooms,
  numberOfNights,
  offers,
  taxDisplayLogic,
  partnerKey,
  label
}: OffersProps) => {
  const dispatch = useDispatch()
  if (!offers) return <ComposedOffersSkeleton />

  const primaryOffer = offers[0]
  const secondaryOffers = offers.slice(1)

  const primaryOfferNightlyDisplayTotalPrice =
    getRoundedNightlyDisplayTotalPrice({
      rate: primaryOffer.rate,
      numberOfNights,
      numberOfRooms,
      taxDisplayLogic
    })

  const offerWithCheapestNightlyPrice = getOfferWithCheapestNightlyPrice({
    offers,
    numberOfNights,
    numberOfRooms,
    taxDisplayLogic
  })

  const isPrimaryOfferHasLowestPrice =
    primaryOffer.url === offerWithCheapestNightlyPrice.url // use offer url as unique identifier

  const trackOfferClick = () => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.Offer,
        action: Action.Clicked,
        payload: {
          partnerKey,
          widgetType: WidgetLabelTypes.hotelCard,
          label
        }
      })
    )
  }

  return (
    <ComposedOffersWrapper container spacing="s000">
      <PrimaryOffer
        price={primaryOfferNightlyDisplayTotalPrice}
        providerCode={getProviderCodeFromName(primaryOffer.provider.name)}
        currency={primaryOffer.currency}
        offer={offers?.[0]}
        includeTaxes={taxDisplayLogic.includeTaxes}
        hasLowestPrice={isPrimaryOfferHasLowestPrice}
        onClick={trackOfferClick}
      />
      <SecondaryOfferList
        offers={secondaryOffers}
        numberOfRooms={numberOfRooms}
        numberOfNights={numberOfNights}
        taxDisplayLogic={taxDisplayLogic}
        onOfferClick={trackOfferClick}
      />
    </ComposedOffersWrapper>
  )
}
